import { useEffect, useState } from 'react';
import { api, type BillingSummary, type PlanOut } from '../lib/api';

/**
 * One allowance, as a sentence and a bar.
 *
 * The bar exists because "18,400 of 120,000" is a number and "nearly out" is a
 * decision, and the creator is here to make the decision. An unmetered tier
 * gets no bar at all rather than an empty one — a bar drawn against nothing
 * reads as "none left", which is the opposite of what NULL means here.
 */
function Left({ label, a, unit, divide = 1 }: {
  label: string; a: { used: number; limit: number | null; soft: boolean }; unit?: string; divide?: number;
}) {
  const n = (v: number) => Math.round(v / divide).toLocaleString('en-US');
  if (a.limit === null) {
    return (
      <p className="sub-dim" style={{ fontSize: 12.5, margin: '4px 0' }}>
        {label}: <b>{n(a.used)}</b> {unit ?? ''} used · <span className="chip">unmetered</span>
      </p>
    );
  }
  const left = Math.max(0, a.limit - a.used);
  const share = Math.min(1, a.limit > 0 ? a.used / a.limit : 1);
  // The same two thresholds the director uses, so the page and the board can
  // never disagree about whether a creator is "low".
  const state = left <= 0 ? 'spent' : share >= 0.85 ? 'low' : 'fine';
  return (
    <div style={{ margin: '6px 0' }}>
      <p style={{ fontSize: 12.5, margin: 0, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>{label}{a.soft && state !== 'fine' ? <span className="chip" style={{ marginLeft: 6 }}>billed past this</span> : null}</span>
        <span className="sub-dim"><b>{n(left)}</b> of {n(a.limit)} {unit ?? ''} left</span>
      </p>
      <div aria-hidden style={{ height: 4, borderRadius: 2, background: 'var(--sunk, #e9ece9)', overflow: 'hidden', marginTop: 3 }}>
        <div style={{
          width: `${Math.round(share * 100)}%`, height: '100%',
          background: state === 'spent' ? 'var(--bad, #c63c17)' : state === 'low' ? 'var(--warn, #b45309)' : 'var(--ok, #00897b)',
        }} />
      </div>
    </div>
  );
}

// The workspace's billing: the tier it has and why, the plans it could
// have, the subscription and invoices Stripe knows about, and the three
// pages Stripe hosts — checkout, the portal, Connect onboarding. Nothing
// here takes a card; every button goes to Stripe and comes back.
export function Billing({ role }: { role: string | null }) {
  const [b, setB] = useState<BillingSummary | null>(null);
  const [plans, setPlans] = useState<PlanOut[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canBuy = role === 'owner' || role === 'admin';
  const load = () => Promise.all([api.billing(), api.plans()]).then(([s, p]) => { setB(s); setPlans(p.plans); }).catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('billing') ?? (q.get('connect') ? `connect-${q.get('connect')}` : null);
    if (flag) {
      setNotice(flag === 'success' ? 'Thank you — Stripe has your subscription; the plan applies within a minute.' : flag === 'cancel' ? 'Checkout was cancelled. Nothing changed.' : flag === 'portal' ? 'Back from the billing portal.' : flag === 'connect-done' ? 'Stripe has your payout details.' : 'Onboarding was interrupted — start again when ready.');
      q.delete('billing'); q.delete('connect');
      window.history.replaceState({}, '', window.location.pathname + (q.toString() ? `?${q}` : '') + window.location.hash);
    }
  }, []);
  const go = async (what: 'checkout' | 'portal' | 'connect', priceId?: string) => {
    setBusy(what + (priceId ?? '')); setError(null);
    try {
      const r = what === 'checkout' ? await api.checkout(priceId!) : what === 'portal' ? await api.portal() : await api.connect();
      window.location.href = r.url;
    } catch (e) { setError((e as Error).message); setBusy(null); }
  };
  const money = (cents: number | null, currency = 'usd') => cents == null ? null : new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
  return (
    <section className="card" data-tool="billing" data-move="plan-tight">
      <h3 data-move="plan-loose">Plan and billing</h3>
      {/* The director points here for all four of the plan and allowance
          moves: what is left this month, and whether the plan still fits. */}
      <div data-move="allowance-low" style={{ display: 'contents' }}>
        <span data-move="allowance-spent" hidden />
      </div>
      {notice && <p className="chip" role="status" style={{ marginBottom: 8 }}>{notice}</p>}
      {error && <div className="err inline">{error}</div>}
      {!b ? <p className="sub-dim">Loading…</p> : (
        <>
          <p>
            This workspace is on <b>{b.tier}</b>
            {b.entitlement?.source === 'override' ? <span className="sub-dim"> · set by the platform{b.entitlement.override?.reason ? `: ${b.entitlement.override.reason}` : ''}</span>
              : b.subscription ? <span className="sub-dim"> · {b.subscription.status}{b.subscription.amount ? ` · ${money(b.subscription.amount, b.subscription.currency)} a ${b.subscription.interval}` : ''}{b.subscription.cancelAtPeriodEnd && b.subscription.periodEnd ? ` · ends ${new Date(b.subscription.periodEnd).toLocaleDateString()}` : b.subscription.periodEnd ? ` · renews ${new Date(b.subscription.periodEnd).toLocaleDateString()}` : ''}</span>
              : <span className="sub-dim"> · no subscription</span>}
          </p>
          {!b.configured && <p className="sub-dim">Billing is not switched on for this deployment yet. The plans below say what each tier gets; nothing can be bought until pricing is set.</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, margin: '10px 0' }}>
            {plans.map((p) => {
              const month = p.prices.find((x) => x.interval === 'month'); const year = p.prices.find((x) => x.interval === 'year');
              const current = b.tier === p.tier;
              return (
                <div key={p.id} style={{ border: `1px solid ${current ? 'var(--blue)' : 'var(--line)'}`, borderRadius: 10, padding: 12 }} data-tool={`plan-${p.tier}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><b>{p.name}</b>{current && <span className="chip">current</span>}</div>
                  <p className="sub-dim" style={{ minHeight: 34 }}>{p.description}</p>
                  <ul style={{ margin: '6px 0', paddingLeft: 16, fontSize: 12.5 }}>{(Array.isArray(p.features) ? p.features as string[] : []).map((f) => <li key={f}>{f}</li>)}</ul>
                  <div style={{ fontSize: 13 }}>{month?.amount != null ? <><b>{money(month.amount, month.currency)}</b> a month{year?.amount != null ? <span className="sub-dim"> · {money(year.amount, year.currency)} a year</span> : null}</> : p.tier === 'free' ? <b>Free</b> : <span className="sub-dim">Pricing to be announced</span>}</div>
                  {p.tier !== 'free' && !current && canBuy && b.configured && month?.amount != null && (
                    <div className="row" style={{ marginTop: 8, gap: 6 }}>
                      <button type="button" className="btn tiny pri" disabled={busy !== null} onClick={() => void go('checkout', month.id)}>Choose monthly</button>
                      {year?.amount != null && <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void go('checkout', year.id)}>Yearly</button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {b.portal && canBuy && b.configured && <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void go('portal')}>Manage payment method and invoices at Stripe</button>}
          {b.invoices.length > 0 && (
            <table style={{ marginTop: 10 }}><thead><tr><th>Invoice</th><th>Status</th><th>Amount</th><th>Period</th><th /></tr></thead>
              <tbody>{b.invoices.map((i) => <tr key={i.id}><td>{new Date(i.createdAt).toLocaleDateString()}</td><td><span className="chip">{i.status}</span></td><td>{money(i.amountPaid || i.amountDue, i.currency)}</td><td className="sub-dim">{i.periodStart ? `${new Date(i.periodStart).toLocaleDateString()} – ${new Date(i.periodEnd ?? i.periodStart).toLocaleDateString()}` : ''}</td><td>{i.url && <a href={i.url} target="_blank" rel="noreferrer">open</a>}</td></tr>)}</tbody></table>
          )}
          {b.trial.onTrial && (
            <p className={b.trial.lapsed ? 'err inline' : 'chip'} style={{ marginTop: 10, display: 'block' }} data-tool="trial" data-move="trial-ending">
              <span data-move="trial-ended" hidden />
              {b.trial.lapsed
                ? <>Your sixty-day trial ended{b.trial.endsAt ? ` on ${new Date(b.trial.endsAt).toLocaleDateString()}` : ''}. Everything you made is still here and still yours to download — choose a plan to make new work.</>
                : <>Trial: <b>{b.trial.daysLeft}</b> {b.trial.daysLeft === 1 ? 'day' : 'days'} left{b.trial.endsAt ? ` · ends ${new Date(b.trial.endsAt).toLocaleDateString()}` : ''}</>}
            </p>
          )}
          <h4 style={{ marginTop: 14 }}>What is left this month</h4>
          {b.credits && !b.credits.unmetered && (
            <p style={{ fontSize: 12.5, margin: '4px 0' }} data-tool="billing-credits">
              Credits: <b>{Math.floor(b.credits.total).toLocaleString()}</b> <span className="sub-dim">({Math.floor(b.credits.plan).toLocaleString()} from the plan, {Math.floor(b.credits.purchased).toLocaleString()} bought) · the Credits page has the ledger and the packs</span>
            </p>
          )}
          <div data-tool="allowances">
            <Left label="Generated pictures" a={b.allowances.images} />
            <Left label="Cloud voice" a={b.allowances.voiceChars} unit="characters" />
            <Left label="Transcription" a={b.allowances.transcriptionSeconds} unit="minutes" divide={60} />
            <Left label="Render minutes" a={b.allowances.renderMinutes} unit="minutes" />
            {/* 186: the ceiling a creator plans around, so the director's two
                episode moves have somewhere to point. */}
            <p className="sub-dim" style={{ fontSize: 12.5, margin: '4px 0' }}
               data-move="episodes-spent"><span data-move="episodes-low" hidden />
              Episodes this month: <b>{b.allowances.episodes.used}</b>
              {b.allowances.episodes.limit !== null ? <> of {b.allowances.episodes.limit}</> : <> · <span className="chip">unmetered</span></>}
            </p>
            <p className="sub-dim" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
              Destinations per show: <b>{b.allowances.platforms.limit ?? 'every one there is'}</b>
              {b.allowances.wastedMinutes > 0 && (
                <> · <b>{b.allowances.wastedMinutes}</b> render minutes lost to our own failures, not charged</>
              )}
            </p>
          </div>
          {b.meteredAt && (
            <p className="sub-dim" style={{ marginTop: 10 }} data-tool="usage">
              Also this month: <b>{b.usage.publishes ?? 0}</b> publishes · <b>{b.usage.live_minutes ?? 0}</b> live minutes · <b>{((b.usage.storage_bytes ?? 0) / 1e9).toFixed(2)} GB</b> stored
              <span> · metered {new Date(b.meteredAt).toLocaleTimeString()}</span>
            </p>
          )}
          <h4 style={{ marginTop: 16 }}>Where your finished work lives</h4>
          <div data-tool="archive">
            {!b.archive ? (
              <>
                <p className="sub-dim" style={{ fontSize: 12.5 }}>
                  Finished episodes can move to <b>your own Google Drive</b> thirty days after they publish.
                  Rookery only ever sees the files it puts there — it cannot read anything else in your Drive.
                </p>
                {canBuy && (
                  <button type="button" className="btn tiny" disabled={busy !== null}
                    onClick={() => { setBusy('drive'); setError(null);
                      api.driveAuthorize().then((r) => { window.location.href = r.url; })
                        .catch((e: Error) => { setError(e.message); setBusy(null); }); }}>
                    Connect Google Drive
                  </button>
                )}
              </>
            ) : (
              <>
                <p style={{ fontSize: 12.5, margin: '4px 0' }}>
                  {b.archive.status === 'connected'
                    ? <>Connected{b.archive.folderUrl ? <> · <a href={b.archive.folderUrl} target="_blank" rel="noreferrer">open the folder</a></> : null}</>
                    : <span className="chip">reconnect needed — {b.archive.status}</span>}
                  {' · '}<b>{b.archive.objectsArchived}</b> archived
                  {b.archive.bytesArchived > 0 && <> ({(b.archive.bytesArchived / 1e9).toFixed(1)} GB)</>}
                  {b.archive.objectsWaiting > 0 && <> · <b>{b.archive.objectsWaiting}</b> waiting</>}
                </p>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, margin: '8px 0' }}>
                  <input type="checkbox" id="archive-keep" checked={b.archive.keepOurCopy} disabled={!canBuy || busy !== null}
                    onChange={(e) => { const keep = e.target.checked; setBusy('keep'); setError(null);
                      api.archiveKeep(keep).then(() => load()).catch((x: Error) => setError(x.message)).finally(() => setBusy(null)); }} />
                  <span>
                    Keep a cold copy at Rookery as well.
                    <span className="sub-dim"> Turning this off means your Drive holds the only copy —
                    if you delete a file there, it is gone. It costs about $0.004 per GB a month to leave on.</span>
                  </span>
                </label>
                {role === 'owner' && (
                  <button type="button" className="btn tiny" disabled={busy !== null}
                    onClick={() => { setBusy('disc'); setError(null);
                      api.archiveDisconnect().then(() => load()).catch((x: Error) => setError(x.message)).finally(() => setBusy(null)); }}>
                    Disconnect
                  </button>
                )}
              </>
            )}
          </div>

          <h4 style={{ marginTop: 14 }}>Paid memberships</h4>
          <p className="sub-dim">Members pay you directly through your own Stripe account; Rookery takes a platform fee on each payment. Onboarding happens on Stripe's pages.</p>
          {b.connect ? (
            <p>Stripe account: {b.connect.chargesEnabled ? <span className="chip">ready to take payments</span> : b.connect.detailsSubmitted ? <span className="chip">under review at Stripe</span> : <span className="chip">onboarding not finished</span>}{b.connect.payoutsEnabled ? <span className="chip" style={{ marginLeft: 4 }}>payouts on</span> : null}
              {!b.connect.chargesEnabled && role === 'owner' && b.configured && <button type="button" className="btn tiny" style={{ marginLeft: 8 }} disabled={busy !== null} onClick={() => void go('connect')}>Continue onboarding</button>}</p>
          ) : role === 'owner' && b.configured ? <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void go('connect')}>Set up payouts with Stripe</button>
            : <p className="sub-dim">{role === 'owner' ? 'Available once billing is switched on.' : 'The workspace owner sets up payouts.'}</p>}
        </>
      )}
    </section>
  );
}
